import * as React from 'react';
import { ListPlus, CalendarPlus, Layout, X, Flag } from 'lucide-react';
import { cn } from './lib/utils';
import { StatusBadge } from './StatusBadge';
import { Button } from './button';

const PRIORITY_VARIANT = {
  critical: 'critical',
  high:     'below-target',
  medium:   'watch',
  low:      'info',
};

const SOURCE_DOMAIN = {
  quality:  'quality',
  staffing: 'staffing',
  access:   'access',
  consumer: 'consumer',
  finance:  'finance',
};

// density controls padding + internal gap + text hierarchy together
const DENSITY_CONFIG = {
  compact:  { padding: 'p-2',   gap: 'gap-1',   titleSize: 'text-[12px]',  descSize: 'text-[11px]' },
  default:  { padding: 'p-3',   gap: 'gap-1.5', titleSize: 'text-body',    descSize: 'text-body-sm' },
  spacious: { padding: 'p-4',   gap: 'gap-2',   titleSize: 'text-body',    descSize: 'text-body-sm' },
};

/**
 * Inbox-style signal card with hover action bar.
 *
 * density="compact" | "default" | "spacious"
 *   Controls card padding and internal content gap together.
 */
function SignalCard({ signal, onQueue, onSchedule, onBoard, onDismiss, onFlag, density = 'default', className }) {
  const [hovered, setHovered] = React.useState(false);
  const dc = DENSITY_CONFIG[density] ?? DENSITY_CONFIG.default;

  return (
    <div
      className={cn(
        'relative rounded-card bg-nn-white border border-nn-border shadow-card',
        dc.padding,
        'transition-shadow hover:shadow-md',
        signal.flagged && 'border-l-2 border-l-yellow-nn',
        className
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={cn('flex items-start', dc.gap)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <StatusBadge variant={PRIORITY_VARIANT[signal.priority] ?? 'info'}>
              {signal.priority}
            </StatusBadge>
            <StatusBadge variant={SOURCE_DOMAIN[signal.source] ?? 'info'}>
              {signal.source}
            </StatusBadge>
            {signal.flagged && (
              <Flag className="h-3 w-3 text-yellow-nn" aria-label="Flagged" />
            )}
          </div>
          <p className={cn('font-medium text-text-dark leading-snug truncate', dc.titleSize)}>{signal.title}</p>
          <p className={cn('text-text-dark-secondary mt-0.5 line-clamp-2', dc.descSize)}>{signal.description}</p>
          {signal.impact && (
            <p className="text-[10px] text-text-muted mt-1">Impact: {signal.impact}</p>
          )}
        </div>
      </div>

      {/* Hover action bar */}
      {hovered && (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end gap-1 px-3 py-1.5 bg-surface/95 border-t border-nn-border rounded-b-card">
          {onQueue && (
            <Button size="icon-sm" variant="ghost" title="Add to queue" aria-label="Add to queue" onClick={() => onQueue(signal.id)}>
              <ListPlus className="h-3.5 w-3.5" />
            </Button>
          )}
          {onSchedule && (
            <Button size="icon-sm" variant="ghost" title="Schedule" aria-label="Schedule" onClick={() => onSchedule(signal.id)}>
              <CalendarPlus className="h-3.5 w-3.5" />
            </Button>
          )}
          {onBoard && (
            <Button size="icon-sm" variant="ghost" title="Add to board" aria-label="Add to board" onClick={() => onBoard(signal.id)}>
              <Layout className="h-3.5 w-3.5" />
            </Button>
          )}
          {onFlag && (
            <Button size="icon-sm" variant="ghost" title="Flag" aria-label="Flag" onClick={() => onFlag(signal.id)}>
              <Flag className="h-3.5 w-3.5" />
            </Button>
          )}
          {onDismiss && (
            <Button size="icon-sm" variant="ghost" title="Dismiss" aria-label="Dismiss" onClick={() => onDismiss(signal.id)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export { SignalCard };
